import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';

// Icons
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { AuthService } from '@shared/modules/auth/services/auth.service';
import { HomepageDataService } from './services/homepage-data.service';
import { NextContestInfo } from './interfaces/next-contest-info';
import { Ranking } from '../ranking/interfaces/ranking';
import { RankingDataService } from '../ranking/services/ranking-data.service';
import { WINDOW } from '@shared/services/window.service';

import { environment } from 'environments/environment';

@Component({
  selector: 'home-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  standalone: false
})
export class HomepageComponent implements OnDestroy, OnInit {

  environment = environment;
  isStudent = false;
  nextContest: NextContestInfo | null = null;
  rankings: Ranking[] = [];
  studentId: number = null;
  updateNextContestInterval = null;

  // Icons
  faSpinner = faSpinner;

  // Progress
  errorReadingNextContest: string | null = null;
  errorReadingRankings: string | null = null;
  isReadingNextContest = false;
  isReadingRankings = false;

  constructor(
    private authService: AuthService,
    private homepageDataService: HomepageDataService,
    @Inject(PLATFORM_ID) private platformId: any,
    private rankingDataService: RankingDataService,
    @Inject(WINDOW) private window: Window | null
  ) { }

  ngOnDestroy() {

    // Stop updating next contest
    if (this.updateNextContestInterval) {
      clearInterval(this.updateNextContestInterval);
      this.updateNextContestInterval = null;
    }
  }

  ngOnInit() {

    // Check whether student is signed in
    this.isStudent = this.authService.isAuthorized(['STUDENT']);
    if (this.isStudent) {
      this.studentId = this.authService.getUserId();
    }

    // Read next contest
    this.readNextContest();

    // Read rankings for leaderboard
    this.readRankings();
  }

  goProfile(studentId: number) {
    if (this.window) {
      this.window.open('/profile?s=' + studentId, '_blank');
    }
  }

  private processRankings(value) {

    // Store the rankings
    this.rankings = value.slice(0, 10);

    // Process values
    this.rankings.forEach(ranking => {
      if (ranking.State === '-') {
        ranking.State = null;
      }

      ranking.Location = ranking.Country + (ranking.State ? ', ' + ranking.State : '');
    });
  }

  private readNextContest() {

    if (this.isReadingNextContest) {
      return;
    }

    // Show spinner
    this.isReadingNextContest = true;

    // Leave if this is not a browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Reset error status
    this.errorReadingNextContest = null;

    // Read the next contest data
    this.homepageDataService.readNext(this.studentId)
      .subscribe(
        (value) => {
          this.nextContest = value;
          this.isReadingNextContest = false;

          // Stop updating the previous next contest
          if (this.updateNextContestInterval) {
            clearInterval(this.updateNextContestInterval);
          }

          // Start updating the new next contest
          this.updateNextContestInterval = setInterval(() => {
            const now = new Date();
            if (now >= this.nextContest.StartUTC) {
              this.readNextContest();
            }
          }, 1000);
        },
        (error) => {
          this.errorReadingNextContest = error;
          this.isReadingNextContest = false;
        }
      );
  }

  private readRankings() {

    if (this.isReadingRankings) {
      return;
    }

    // Show spinner
    this.isReadingRankings = true;

    // Leave if this is not a browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Reset error status
    this.errorReadingRankings = null;

    // Read the ranking data
    this.rankingDataService.read()
      .subscribe(
        (value) => {

            this.processRankings(value);

            // Hide spinner
            this.isReadingRankings = false;
        },
        (error) => {
            this.errorReadingRankings = error;
            this.isReadingRankings = false;
        }
      );
  }
}
